// src/components/ClientManagementDashboard.js

import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, Button, Tabs, Tab, CircularProgress, Alert } from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase'; // Ensure this path is correct

// Import your tab panel components
// Corrected imports for ClientManagementDashboard.js
import ClientSetupChecklist from './Admin/ClientSetupChecklist';
import ClientInformationView from './ClientInformationView';
import UnifiedClientDocumentHistory from './UnifiedClientDocumentHistory';
import ClientProcessingTools from './Admin/ClientProcessingTools'; // Now points to the new file
import AdminExistingQuestions from './Admin/AdminExistingQuestions';


const ClientManagementDashboard = ({ user, client, onBack, onClientUpdate }) => {
    const [activeTab, setActiveTab] = useState(0);
    const [documents, setDocuments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const fetchClientDocuments = useCallback(async () => {
        if (!client?.id) return;
        setLoading(true);
        setError(null);
        try {
            const docsRef = collection(db, 'documents');
            const q = query(docsRef, where('clientId', '==', client.id));
            const querySnapshot = await getDocs(q);
            const fetchedDocs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setDocuments(fetchedDocs);
        } catch (err) {
            console.error("Error fetching client documents:", err);
            setError("Failed to load client documents. Please try again.");
        } finally {
            setLoading(false);
        }
    }, [client?.id]);

    useEffect(() => {
        fetchClientDocuments();
    }, [fetchClientDocuments]);

    const handleDataRefresh = () => {
        onClientUpdate(); // Refresh parent data
        fetchClientDocuments(); // Refresh local documents
    };

    if (!client) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="warning">No client selected.</Alert>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mt: 2 }}>
                    Back to User Details
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mr: 2 }}>
                    Back to User Details
                </Button>
                <Typography variant="h5" component="h1">
                    Manage Client: {client.name}
                </Typography>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', width: '100%' }}>
                <Tabs value={activeTab} onChange={handleTabChange} aria-label="Client Management Tabs">
                    <Tab label="Setup Checklist" />
                    <Tab label="Profile & Documents" />
                    <Tab label="Q&A" />
                    <Tab label="Processing Tools" />
                </Tabs>
            </Box>

            {/* Tab Content */}
            <Box sx={{ pt: 3, flexGrow: 1, overflowY: 'auto' }}>
                {activeTab === 0 && (
    <ClientSetupChecklist
        client={client}
        user={user}  // <-- ADD THIS LINE
        documents={documents}
        onClientUpdate={handleDataRefresh}
    />
)}
                {activeTab === 1 && (
  <>
    <ClientInformationView
      client={client} 
      user={user}
      onClientUpdate={handleDataRefresh}
    />
    {/* THE DOCUMENT LIST IS NOW RENDERED HERE */}
    <Typography variant="h6" sx={{ mt: 4, mb: 2 }}>Document History</Typography>
                        {loading && <CircularProgress />}
                        {error && <Alert severity="error">{error}</Alert>}
                        {!loading && !error && (
    <>
        {console.log('[DEBUG] Client active status check:', { 
            'client.active': client.active, 
            'client.status': client.status,
            'clientIsActive result': client.active === true 
        })}
        <UnifiedClientDocumentHistory
            clientId={client.id}
            user={user}
            documents={documents} // Pass documents to history
            clientIsActive={client.active === true}
            onUploadSuccess={fetchClientDocuments}
            onDocumentUpdate={fetchClientDocuments}
        />
    </>
)}
                    </>
                )}
                {activeTab === 2 && (
  <AdminExistingQuestions
    clientId={client.id}
    user={user}
    isAdmin={true}
    clientIsActive={client.active === true}
  />
)}
                {activeTab === 3 && <ClientProcessingTools clientId={client.id} onUpdate={handleDataRefresh} />}
            </Box>
        </Box>
    );
};

export default ClientManagementDashboard;